import { Button } from '@/components/shadcn/button';
import { useRTL } from '@/hooks';
import { CarouselBlock } from '@/utils/llm-schema';
import { Card, CardContent, CardHeader } from 'components/shadcn/card';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from 'components/shadcn/carousel';
import { CircleDot, Dot } from 'lucide-react';
import { useEffect, useState } from 'react';

const DOT_SIZE = 16 as const;

const MaybeLink = ({ href, children }: { href: string | null; children: React.ReactNode }) =>
  href ? (
    <Button className="w-fit h-fit" variant={'ghost'} asChild>
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    </Button>
  ) : (
    <>{children}</>
  );

export function MessageCarousel({ list }: { list: CarouselBlock['images'] }) {
  const { isRTL } = useRTL();
  const showNav = list.length > 1;
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap() + 1);

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  return (
    <div>
      <Carousel
        dir="ltr"
        setApi={setApi}
        opts={{
          align: 'center',
        }}
        className="my-2"
      >
        <CarouselContent className="">
          {list.map((e, index) => (
            <CarouselItem key={index} className="flex justify-center items-center">
              <MaybeLink href={e.itemLink}>
                <Card className="w-fit mx-2">
                  <CardHeader className="p-2 justify-center flex items-center">
                    <img
                      className="rounded-lg object-cover h-40 max-w-40"
                      src={e.imageLink}
                      alt={e.title}
                    />
                  </CardHeader>
                  <CardContent className="max-w-40" dir={e.dir}>
                    <p className="font-bold truncate">{e.title}</p>
                    <p className="truncate">{e.description}</p>
                  </CardContent>
                </Card>
              </MaybeLink>
            </CarouselItem>
          ))}
        </CarouselContent>
        {showNav && (
          <>
            <CarouselPrevious className="left-2" />
            <CarouselNext className="right-2" />
          </>
        )}
      </Carousel>
      <div dir={isRTL ? 'rtl' : 'ltr'} className="flex justify-center gap-2 pb-2">
        {list.length > 1 &&
          list.map((e, index) => {
            if (index + 1 !== current)
              return (
                <Button
                  onClick={() => api?.scrollTo(index)}
                  variant={'ghost'}
                  className="p-2"
                  key={e.title + index}
                >
                  <Dot size={DOT_SIZE} />
                </Button>
              );

            return (
              <Button className="p-2" variant={'ghost'} key={e.title + index}>
                <CircleDot size={DOT_SIZE} />
              </Button>
            );
          })}
      </div>
    </div>
  );
}
